import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Bugs = () => {
  const { user } = useAuth();
  const [bugs, setBugs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [newBug, setNewBug] = useState({ title: '', description: '', priority: 'medium' });
  const [loading, setLoading] = useState(true);
  
  // Edit modal state
  const [editModal, setEditModal] = useState(null);
  const [editBug, setEditBug] = useState({ title: '', description: '', priority: '', status: '' });

  // Check for dark mode from localStorage
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('darkMode') === 'true';
  });

  const textColor = darkMode ? '#f3f4f6' : '#111827';
  const cardBg = darkMode ? '#1f2937' : '#ffffff';
  const borderColor = darkMode ? '#374151' : '#e5e7eb';
  const inputBg = darkMode ? '#374151' : '#fff';
  const inputBorder = darkMode ? '#4b5563' : '#d1d5db';

  const fetchBugs = async () => {
    try {
      const res = await fetch('/api/bugs');
      const data = await res.json();
      setBugs(data);
    } catch (err) {
      console.error('Failed to fetch bugs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBugs();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/bugs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newBug)
      });
      if (res.ok) {
        setNewBug({ title: '', description: '', priority: 'medium' });
        setShowForm(false);
        fetchBugs();
      }
    } catch (err) {
      console.error('Failed to create bug:', err);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await fetch('/api/bugs/' + id, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      fetchBugs();
    } catch (err) {
      console.error('Failed to update bug:', err);
    }
  };

  const openEditModal = (bug) => {
    setEditBug({ title: bug.title, description: bug.description, priority: bug.priority, status: bug.status });
    setEditModal(bug.id);
  };

  const handleEditSubmit = async (id) => {
    try {
      const res = await fetch('/api/bugs/' + id, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editBug)
      });
      if (res.ok) {
        setEditModal(null);
        fetchBugs();
      }
    } catch (err) {
      console.error('Failed to update bug:', err);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#ef4444';
      case 'medium': return '#eab308';
      case 'low': return '#22c55e';
      default: return '#6b7280';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return { bg: '#fef2f2', text: '#dc2626' };
      case 'in_progress': return { bg: '#fefce8', text: '#ca8a04' };
      case 'resolved': return { bg: '#f0fdf4', text: '#16a34a' };
      case 'closed': return { bg: '#f3f4f6', text: '#6b7280' };
      default: return { bg: '#f3f4f6', text: '#6b7280' };
    }
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  return (
    <div className="page">
      <Link to="/" className="btn btn-secondary mb-4">← Back to Dashboard</Link>
      <div className="page-header">
        <h1 style={{ color: textColor }}>🐛 Bug Reports & Feature Requests</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ New Issue'}
        </button>
      </div>

      {showForm && (
        <div className="card mb-4" style={{ background: cardBg, borderColor }}>
          <h3 style={{ color: textColor }}>Report an Issue</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label style={{ color: textColor }}>Title</label>
              <input
                type="text"
                className="form-control"
                style={{ background: inputBg, borderColor, color: textColor }}
                value={newBug.title}
                onChange={(e) => setNewBug({ ...newBug, title: e.target.value })}
                placeholder="Brief description of the issue"
                required
              />
            </div>
            <div className="form-group">
              <label style={{ color: textColor }}>Description</label>
              <textarea
                className="form-control"
                style={{ background: inputBg, borderColor, color: textColor }}
                rows="4"
                value={newBug.description}
                onChange={(e) => setNewBug({ ...newBug, description: e.target.value })}
                placeholder="Detailed description..."
              />
            </div>
            <div className="form-group">
              <label style={{ color: textColor }}>Priority</label>
              <select
                className="form-control"
                style={{ background: inputBg, borderColor, color: textColor }}
                value={newBug.priority}
                onChange={(e) => setNewBug({ ...newBug, priority: e.target.value })}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <button type="submit" className="btn btn-primary">Submit Issue</button>
          </form>
        </div>
      )}

      {loading ? (
        <p style={{ color: textColor }}>Loading...</p>
      ) : bugs.length === 0 ? (
        <div className="card" style={{ background: cardBg, borderColor }}>
          <p className="text-muted">No issues reported yet. Click "+ New Issue" to add one.</p>
        </div>
      ) : (
        <div className="bugs-list">
          {bugs.map((bug) => {
            const statusColors = getStatusColor(bug.status);
            const priorityColor = getPriorityColor(bug.priority);
            return (
              <div key={bug.id} className="card mb-2" style={{ background: cardBg, borderColor }}>
                <div className="bug-header">
                  <h3 style={{ color: textColor }}>{bug.title}</h3>
                  <span style={{ 
                    background: priorityColor, 
                    color: '#fff', 
                    padding: '4px 10px', 
                    borderRadius: 12, 
                    fontSize: 11, 
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {bug.priority}
                  </span>
                </div>
                <p style={{ color: textColor, opacity: 0.8 }}>{bug.description}</p>
                <div className="bug-meta" style={{ color: textColor, opacity: 0.6 }}>
                  <span style={{ background: statusColors.bg, color: statusColors.text, padding: '2px 8px', borderRadius: 4, fontSize: 12 }}>
                    {bug.status.replace('_', ' ')}
                  </span>
                  <span>Created by {bug.createdBy}</span>
                  <span>{new Date(bug.createdAt).toLocaleDateString()}</span>
                </div>
                <div className="bug-actions mt-2" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {isAdmin && (
                    <>
                      <select
                        value={bug.status}
                        onChange={(e) => handleStatusChange(bug.id, e.target.value)}
                        className="form-control"
                        style={{ background: inputBg, borderColor, color: textColor, flex: 1, minWidth: '120px' }}
                      >
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                      <button 
                        className="btn btn-secondary"
                        onClick={() => openEditModal(bug)}
                        style={{ padding: '6px 12px' }}
                      >
                        ✏️ Edit
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Modal */}
      {editModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ background: cardBg, borderColor, maxWidth: 500, width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
            <h3 style={{ color: textColor }}>Edit Issue</h3>
            <div className="form-group">
              <label style={{ color: textColor }}>Title</label>
              <input
                type="text"
                className="form-control"
                style={{ background: inputBg, borderColor, color: textColor }}
                value={editBug.title}
                onChange={(e) => setEditBug({ ...editBug, title: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label style={{ color: textColor }}>Description</label>
              <textarea
                className="form-control"
                style={{ background: inputBg, borderColor, color: textColor }}
                rows="4"
                value={editBug.description}
                onChange={(e) => setEditBug({ ...editBug, description: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label style={{ color: textColor }}>Priority</label>
              <select
                className="form-control"
                style={{ background: inputBg, borderColor, color: textColor }}
                value={editBug.priority}
                onChange={(e) => setEditBug({ ...editBug, priority: e.target.value })}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div className="form-group">
              <label style={{ color: textColor }}>Status</label>
              <select
                className="form-control"
                style={{ background: inputBg, borderColor, color: textColor }}
                value={editBug.status}
                onChange={(e) => setEditBug({ ...editBug, status: e.target.value })}
              >
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
              <button className="btn btn-primary" onClick={() => handleEditSubmit(editModal)}>Save Changes</button>
              <button className="btn btn-secondary" onClick={() => setEditModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bugs;